import { X } from 'lucide-react';
import { AnimatePresence, motion } from 'motion/react';
import { useEffect, useRef, useState } from 'react';

import {
  Button,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  cn,
} from '@workspace/shadcn';

import { useCourses } from '../../lib/api.ts';
import {
  FORMSPREE_ENDPOINT,
  positionItems,
  validReferralCodes,
} from '../constants/contact.ts';
import { useModals } from './modals.tsx';

export function ContactModal() {
  const { openModal, close } = useModals();
  const type = openModal === 'course' ? 'course' : 'contact';
  const isOpen = openModal === 'contact' || openModal === 'course';

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [referralCode, setReferralCode] = useState('');
  const [isValidReferral, setIsValidReferral] = useState<boolean | null>(null);
  const [isValidating, setIsValidating] = useState(false);

  const initialFocusRef = useRef<HTMLInputElement>(null);
  const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const coursesQuery = useCourses();

  const validateReferralCode = (code: string) => {
    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);

    if (code === '') {
      setIsValidReferral(null);
      setIsValidating(false);
      return;
    }

    setIsValidating(true);
    setIsValidReferral(null);

    debounceTimerRef.current = setTimeout(() => {
      setIsValidReferral(validReferralCodes.includes(code.trim()));
      setIsValidating(false);
    }, 500);
  };

  useEffect(
    () => () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    },
    [],
  );

  useEffect(() => {
    if (isOpen) initialFocusRef.current?.focus();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') close();
    };

    document.addEventListener('keydown', handleKeyDown);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [isOpen, close]);

  const handleClose = () => {
    close();
    setSubmitted(false);
    setReferralCode('');
    setIsValidReferral(null);
    setIsValidating(false);
  };

  // Formspree is posted to through a hidden iframe so the visitor stays on the
  // page; there is no response to read, hence the timed success state.
  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (type === 'course' && isValidReferral === false) return;

    const form = event.currentTarget;
    setIsSubmitting(true);
    form.target = 'contact-form-target';
    form.submit();

    setTimeout(() => {
      form.reset();
      setReferralCode('');
      setIsValidReferral(null);
      setIsSubmitting(false);
      setSubmitted(true);
      setTimeout(handleClose, 3000);
    }, 2000);
  };

  const courses = coursesQuery.data?.records ?? [];

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[1000] flex items-center justify-center overflow-y-auto bg-black/60 p-4 backdrop-blur-sm"
          onClick={handleClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ type: 'spring', damping: 15, stiffness: 300 }}
            className="bg-card w-full max-w-md rounded-lg p-6 shadow-lg"
            onClick={(event) => event.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="contact-modal-title"
          >
            <div className="mb-4 flex items-center justify-between">
              <h2 id="contact-modal-title" className="text-2xl font-bold">
                {type === 'course' ? 'Notify Me' : 'Contact Us'}
              </h2>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleClose}
                aria-label="Close modal"
              >
                <X className="h-4 w-4" />
              </Button>
            </div>

            <form
              onSubmit={handleSubmit}
              action={FORMSPREE_ENDPOINT}
              method="POST"
              encType="multipart/form-data"
              className="space-y-2"
            >
              <div>
                <Label htmlFor="name">
                  Name <span className="text-primary">*</span>
                </Label>
                <Input
                  id="name"
                  name="name"
                  ref={initialFocusRef}
                  placeholder="Your name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="email">
                  Email <span className="text-primary">*</span>
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="Your email"
                  required
                />
              </div>
              <div>
                <Label htmlFor="phone">
                  Phone Number <span className="text-primary">*</span>
                </Label>
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="Your phone number"
                  required
                />
              </div>

              {type === 'course' && (
                <>
                  <div>
                    <Label htmlFor="course">
                      Course <span className="text-primary">*</span>
                    </Label>
                    <Select
                      name="course"
                      required
                      defaultValue="applied-healthcare-ai-associate-program"
                    >
                      <SelectTrigger id="course">
                        <SelectValue placeholder="Select a course" />
                      </SelectTrigger>
                      <SelectContent className="z-[1001]">
                        {courses.map((course) => (
                          <SelectItem key={course.slug} value={course.slug}>
                            {course.title}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="position">
                      Your Position <span className="text-primary">*</span>
                    </Label>
                    <Select name="position" required>
                      <SelectTrigger id="position">
                        <SelectValue placeholder="Select your position" />
                      </SelectTrigger>
                      <SelectContent className="z-[1001]">
                        {positionItems.map((item) => (
                          <SelectItem key={item.value} value={item.value}>
                            {item.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="referralCode">
                      Referral Code{' '}
                      <span className="text-muted-foreground">(Optional)</span>
                    </Label>
                    <Input
                      id="referralCode"
                      name="referralCode"
                      value={referralCode}
                      onChange={(event) => {
                        setReferralCode(event.target.value);
                        validateReferralCode(event.target.value);
                      }}
                      className={cn({
                        'border-red-500 focus-visible:ring-red-500':
                          isValidReferral === false,
                        'border-green-500 focus-visible:ring-green-500':
                          isValidReferral === true,
                      })}
                      placeholder="Enter referral code if you have one"
                    />
                    {isValidating && (
                      <p className="text-muted-foreground mt-2 text-xs">
                        Validating referral code...
                      </p>
                    )}
                    {isValidReferral === true && (
                      <p className="mt-2 text-xs text-green-500">
                        &#10003; Valid referral code
                      </p>
                    )}
                    {isValidReferral === false && (
                      <p className="text-destructive mt-2 text-xs">
                        &#10007; Invalid referral code. You can leave it empty
                        or enter a valid one.
                      </p>
                    )}
                  </div>
                </>
              )}

              {type === 'contact' && (
                <div>
                  <Label htmlFor="message">
                    Message <span className="text-primary">*</span>
                  </Label>
                  <Textarea
                    id="message"
                    name="message"
                    placeholder="Your message"
                    required
                  />
                </div>
              )}

              <Button
                type="submit"
                className="w-full"
                disabled={
                  isSubmitting || (type === 'course' && isValidReferral === false)
                }
              >
                {isSubmitting ? 'Sending...' : 'Send Message'}
              </Button>

              {submitted && (
                <p className="text-primary text-center">
                  Message sent successfully!
                </p>
              )}
            </form>

            <iframe
              name="contact-form-target"
              title="Form submission target"
              className="hidden"
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
